import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BookMarked,
  Search,
  Loader2,
  Sparkles,
  ExternalLink,
  AlertTriangle,
  Copy,
  Plus,
  Thermometer,
  ShieldCheck,
} from "lucide-react";

const SECTIONS = [
  { id: "marco_teorico", label: "Marco Teórico" },
  { id: "estado_del_arte", label: "Estado del Arte" },
  { id: "antecedentes", label: "Antecedentes" },
  { id: "discusion", label: "Discusión" },
];

export default function RagPanel({ projectId, defaultQuery = "", onInsertIntoEditor }) {
  const [query, setQuery] = useState(defaultQuery);
  const [section, setSection] = useState("marco_teorico");
  const [maxSources, setMaxSources] = useState(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/research/rag`, {
        query: query.trim(),
        section,
        max_sources: maxSources,
      });
      setResult(data);
      if (!data.sources?.length) {
        toast.error("La IA no encontró fuentes con abstract para esta búsqueda");
      } else {
        toast.success(`${data.sources.length} fuentes reales recuperadas`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo recuperar la investigación");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!result) return;
    try {
      const refs = result.sources
        .map((s) => `${s.rank}. ${s.apa_reference}`)
        .join("\n");
      const blob = `${result.synthesis}\n\nReferencias\n${refs}`;
      await navigator.clipboard.writeText(blob);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const insertIntoEditor = () => {
    if (!result || typeof onInsertIntoEditor !== "function") return;
    // Build a HTML block: synthesis paragraphs + references
    const paragraphs = result.synthesis
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
      .join("");
    const refs = result.sources
      .map((s) => `<li>${escapeHtml(s.apa_reference)}</li>`)
      .join("");
    const html = `${paragraphs}<h3>Referencias</h3><ol>${refs}</ol>`;
    onInsertIntoEditor(html);
    toast.success("Bloque insertado al final del editor");
  };

  return (
    <div className="h-full overflow-y-auto p-5 flex flex-col gap-4 bg-paper-2/30" data-testid="rag-panel">
      <header>
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-7 w-7 rounded-md bg-forest text-paper">
            <BookMarked size={13} />
          </span>
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
              Investigar con fuentes reales · RAG
            </p>
            <h3 className="font-serif text-base text-ink leading-tight">
              Generación basada en abstracts reales
            </h3>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-white border border-rule px-2 py-0.5 text-[10px] text-ink-soft">
            <Thermometer size={10} /> temperature 0.0
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white border border-rule px-2 py-0.5 text-[10px] text-ink-soft">
            <ShieldCheck size={10} /> Sin invenciones
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white border border-rule px-2 py-0.5 text-[10px] text-ink-soft">
            OpenAlex · Semantic Scholar
          </span>
        </div>
      </header>

      {/* Form */}
      <div className="rounded-xl border border-rule bg-white p-3 flex flex-col gap-3">
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-ink-soft font-medium">
            Tema o pregunta de investigación
          </span>
          <textarea
            data-testid="rag-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={3}
            placeholder="Ej. flipped classroom higher education learning outcomes"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-rule bg-paper-2/30 focus:bg-white text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 resize-none"
          />
          <p className="mt-1 text-[10.5px] text-ink-soft">
            Tip: consulta más resultados si usas términos en inglés.
          </p>
        </label>

        <div className="flex items-center gap-2">
          <select
            data-testid="rag-section"
            value={section}
            onChange={(e) => setSection(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest"
          >
            {SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            data-testid="rag-max-sources"
            value={maxSources}
            onChange={(e) => setMaxSources(parseInt(e.target.value, 10))}
            className="w-24 px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest"
          >
            {[3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {n} fuentes
              </option>
            ))}
          </select>
        </div>

        <button
          data-testid="rag-run-btn"
          onClick={run}
          disabled={!query.trim() || loading}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60 transition-all shadow-sm"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
          {loading
            ? "Recuperando abstracts y redactando…"
            : "Investigar con fuentes reales"}
        </button>
      </div>

      {/* Result */}
      {loading && (
        <div
          data-testid="rag-loading"
          className="rounded-xl border border-rule bg-white p-4 text-[12.5px] text-ink-soft flex flex-col gap-2"
        >
          <span className="inline-flex items-center gap-2">
            <Loader2 size={13} className="animate-spin text-forest" />
            Buscando en OpenAlex…
          </span>
          <span className="inline-flex items-center gap-2 text-ink-soft/80">
            <Loader2 size={13} className="animate-spin text-copper" />
            Pasando abstracts a Claude (temperature 0)…
          </span>
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-3" data-testid="rag-result">
          {/* Grounding banner */}
          <div className="rounded-xl border border-forest/20 bg-white p-3 text-[11.5px] text-ink-soft flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-forest" />
              <span className="text-ink font-medium">
                {result.sources.length} fuentes reales
              </span>
              · grounding: {result.grounding}
            </span>
            <span>· {result.citation_format}</span>
            <span>· temperature {result.model_temperature}</span>
            <button
              data-testid="rag-copy"
              onClick={copyToClipboard}
              className="ml-auto inline-flex items-center gap-1 text-forest hover:text-forest-dark"
            >
              <Copy size={11} /> Copiar
            </button>
            {typeof onInsertIntoEditor === "function" && (
              <button
                data-testid="rag-insert"
                onClick={insertIntoEditor}
                className="inline-flex items-center gap-1 text-forest hover:text-forest-dark"
              >
                <Plus size={11} /> Insertar
              </button>
            )}
          </div>

          {/* Synthesis */}
          <div className="rounded-xl border border-rule bg-white p-4">
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-2">
              Síntesis generada con citas APA inline
            </p>
            <article
              data-testid="rag-synthesis"
              className="prose-thesis text-[13.5px] text-ink leading-relaxed whitespace-pre-wrap"
            >
              {result.synthesis}
            </article>
          </div>

          {/* Sources */}
          <div className="rounded-xl border border-rule bg-white p-4">
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
              Fuentes recuperadas (verificables)
            </p>
            <ol className="flex flex-col gap-3" data-testid="rag-sources">
              {result.sources.map((s) => (
                <li
                  key={s.rank}
                  data-testid={`rag-source-${s.rank}`}
                  className="border-l-2 border-copper/40 pl-3"
                >
                  <p className="font-serif text-[14px] text-ink leading-snug">
                    {s.title}
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-ink-soft">
                    {s.apa_inline}
                    {typeof s.citation_count === "number" && (
                      <> · {s.citation_count.toLocaleString("es-MX")} citas</>
                    )}
                    {s.venue ? <> · {s.venue}</> : null}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {s.doi && (
                      <a
                        href={`https://doi.org/${s.doi}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-forest hover:text-forest-dark"
                      >
                        <ExternalLink size={10} /> DOI: {s.doi}
                      </a>
                    )}
                    {s.url && (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-forest hover:text-forest-dark"
                      >
                        <ExternalLink size={10} /> Ver fuente
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Not found in sources */}
          {result.not_found_in_sources?.length > 0 && (
            <div
              data-testid="rag-not-found"
              className="rounded-xl border border-copper/30 bg-[#FBF6EC] p-4"
            >
              <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle size={11} /> Datos NO encontrados en las fuentes
              </p>
              <ul className="flex flex-col gap-1.5">
                {result.not_found_in_sources.map((n, i) => (
                  <li
                    key={i}
                    className="text-[12.5px] text-ink leading-snug pl-3 border-l-2 border-copper/40"
                  >
                    {n}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11px] text-ink-soft italic">
                La IA NO inventó estos puntos. Tendrás que buscar fuentes adicionales o cambiar los términos de búsqueda.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
